import {
  ImgMovement,
  ImgMovementDisabled,
  TADirectTypeEnum,
  UiHelpersService,
  UiModule,
} from '@shared/ui';
import { Subject, takeUntil } from 'rxjs';

import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { DirectCollectService } from '../../services/direct-collect.service';
import { TimelineDetailMessageComponent } from '../timeline-detail-message/timeline-detail-message.component';
import { TimelineDetailMessagesComponent } from '../timeline-detail-messages/timeline-detail-messages.component';
import { TimelineDetailComponent } from '../timeline-detail/timeline-detail.component';

@Component({
  selector: 'phoenix-timeline-detail-container',
  imports: [
    TimelineDetailMessagesComponent,
    TimelineDetailMessageComponent,
    TimelineDetailComponent,
    UiModule,
  ],
  templateUrl: './timeline-detail-container.component.html',
})
export class TimelineDetailContainerComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() timelineSelectedItem: any | null = null;
  @Input() removeMovement: boolean = false;
  @Output() endOfScroll = new EventEmitter<void>();

  uiHelper = inject(UiHelpersService);
  timelineService = inject(DirectCollectService);
  dialog = inject(MatDialog);

  taDirectTypeEnum = TADirectTypeEnum;

  timelineItem: any | null = null;

  destroy$ = new Subject<void>();

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (
      this.removeMovement ||
      this.uiHelper.isInputFocused(event.target as HTMLElement) ||
      this.dialog.openDialogs.length > 0 ||
      !this.timelineItem
    ) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      if (
        this.movementDisabled !== 'previous' &&
        this.movementDisabled !== 'both'
      ) {
        this.onImgMovement('previous');
        event.preventDefault();
      }
    }

    if (event.key === 'ArrowRight') {
      if (
        this.movementDisabled !== 'next' &&
        this.movementDisabled !== 'both'
      ) {
        this.onImgMovement('next');
        event.preventDefault();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.removeMovement && changes['timelineSelectedItem']) {
      const currentValue = changes['timelineSelectedItem'].currentValue;
      if (currentValue && currentValue.id?.length > 0) {
        this.timelineItem = currentValue;
      } else {
        this.timelineItem = null;
      }
    }
  }

  ngOnInit() {
    if (!this.removeMovement) {
      this.timelineService.timelineSelectedItem$
        .pipe(takeUntil(this.destroy$))
        .subscribe(item => {
          this.timelineItem = item;
        });
    }
  }

  get movementDisabled(): ImgMovementDisabled {
    const actualIndex = this.imgIndex;
    const timelineResults = this.timelineService.filteredTimelineResults$.value;
    if (actualIndex > -1) {
      const isFirst = actualIndex === 0;
      const isLast = actualIndex === timelineResults.length - 1;

      if (isFirst && isLast) {
        // Only one image, disable both directions
        return 'both';
      } else if (isFirst) {
        // First image, disable previous movement
        return 'previous';
      } else if (isLast) {
        // Last image, disable next movement
        return 'next';
      } else {
        // Middle image, enable both directions
        return null;
      }
    }
    return 'both';
  }

  get imgIndex(): number {
    const timelineResults = this.timelineService.filteredTimelineResults$.value;
    if (!timelineResults?.length || !this.timelineItem) {
      return -1;
    }

    return timelineResults.findIndex(item => item.id === this.timelineItem?.id);
  }

  onImgMovement(move: ImgMovement) {
    const timelineResults = this.timelineService.filteredTimelineResults$.value;
    const actualIndex = this.imgIndex;

    if (actualIndex !== -1) {
      // Check if we're at or beyond 75% of the total list
      const seventyFivePercentThreshold = Math.floor(
        timelineResults.length * 0.75
      );
      if (actualIndex >= seventyFivePercentThreshold) {
        this.endOfScroll.emit();
      }

      let nextInTimeline = null;
      if (move === 'next' && actualIndex < timelineResults.length - 1) {
        // Move to the next item
        nextInTimeline = timelineResults[actualIndex + 1];
        this.timelineService.timelineSelectedItem$.next(nextInTimeline);
      } else if (move === 'previous' && actualIndex > 0) {
        // Move to the previous item
        nextInTimeline = timelineResults[actualIndex - 1];
        this.timelineService.timelineSelectedItem$.next(nextInTimeline);
      }

      if (nextInTimeline?.id) {
        this.uiHelper.navigateToElement(nextInTimeline.id);
      }

      if (
        nextInTimeline?.type !== TADirectTypeEnum.SCREENSHOT &&
        this.dialog.openDialogs.length > 0
      ) {
        this.dialog.closeAll();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
